import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Trash2, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import api, { assetUrl, formatApiErrorDetail } from "@/api";
import { CAT_LABELS, GALLERY_FILTERS } from "@/data/content";

export default function AdminGalleryManager() {
  const [title, setTitle] = useState("");
  const [cat, setCat] = useState("perforazioni");
  const [files, setFiles] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["gallery"],
    queryFn: async () => (await api.get("/gallery")).data,
  });

  const upload = async (e) => {
    e.preventDefault();
    if (uploading) return;
    if (!files || files.length === 0) {
      toast.error("Seleziona almeno una foto.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("title", title);
      fd.append("cat", cat);
      for (const f of files) fd.append("files[]", f);
      const { data: created } = await api.post("/admin/gallery", fd);
      toast.success(`Caricate ${created.length} immagini in galleria!`);
      setTitle("");
      setFiles(null);
      const input = document.getElementById("admin-gallery-files");
      if (input) input.value = "";
      queryClient.invalidateQueries({ queryKey: ["gallery"] });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
    }
  };

  const remove = async (item) => {
    if (deleting) return;
    setDeleting(item.id);
    try {
      await api.delete(`/admin/gallery/${item.id}`);
      toast.success("Immagine rimossa dalla galleria.");
      queryClient.invalidateQueries({ queryKey: ["gallery"] });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setDeleting(null);
    }
  };

  const inputCls =
    "w-full border border-white/15 bg-brand-slate/60 px-4 py-3.5 text-sm text-white placeholder:text-white/35 outline-none transition focus:border-brand-ice focus:ring-1 focus:ring-brand-ice";

  return (
    <div data-testid="admin-gallery-manager">
      <form onSubmit={upload} className="max-w-3xl border border-white/10 bg-brand-card p-8" data-testid="admin-gallery-upload-form">
        <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-white">
          Carica un album di lavori
        </h2>
        <p className="mt-2 text-sm text-slate-400">
          Puoi selezionare più foto insieme: finiranno tutte in galleria nella categoria scelta.
        </p>
        <div className="mt-6 grid gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor="ag-title" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
              Titolo (es. nome del cantiere)
            </label>
            <input
              id="ag-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Es. Cantiero SS7 — palificazioni"
              className={inputCls}
              data-testid="admin-gallery-title-input"
            />
          </div>
          <div>
            <label htmlFor="ag-cat" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
              Categoria
            </label>
            <select
              id="ag-cat"
              value={cat}
              onChange={(e) => setCat(e.target.value)}
              className={inputCls}
              data-testid="admin-gallery-cat-select"
            >
              {GALLERY_FILTERS.filter((f) => f.id !== "tutte").map((f) => (
                <option key={f.id} value={f.id} className="bg-brand-slate">
                  {CAT_LABELS[f.id]}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-5">
          <label htmlFor="admin-gallery-files" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
            Foto (JPG, PNG o WebP — selezione multipla)
          </label>
          <input
            id="admin-gallery-files"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            onChange={(e) => setFiles(e.target.files ? Array.from(e.target.files) : null)}
            className="w-full text-xs text-slate-400 file:mr-3 file:border file:border-white/20 file:bg-transparent file:px-3 file:py-2 file:font-mono file:text-[10px] file:uppercase file:tracking-[0.2em] file:text-white hover:file:border-brand-cyan"
            data-testid="admin-gallery-files-input"
          />
        </div>
        <button
          type="submit"
          disabled={uploading}
          data-testid="admin-gallery-upload-button"
          className="mt-6 flex items-center gap-3 bg-brand-cyan px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-brand-teal transition hover:bg-brand-ice disabled:cursor-not-allowed disabled:opacity-60"
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
          Carica in galleria
        </button>
      </form>

      <h2 className="mt-14 font-display text-2xl font-bold uppercase tracking-tight text-white">
        Immagini in galleria
      </h2>
      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-7 w-7 animate-spin text-brand-cyan" />
        </div>
      ) : (
        <div className="mt-8 grid gap-6 sm:grid-cols-3 lg:grid-cols-4" data-testid="admin-gallery-list">
          {(data || []).map((g) => (
            <figure key={g.id} className="border border-white/10 bg-brand-card p-3">
              <img src={assetUrl(g.image_url)} alt={g.title} className="aspect-[4/3] w-full object-cover" loading="lazy" />
              <figcaption className="mt-3">
                <p className="truncate text-sm text-white" title={g.title}>{g.title}</p>
                <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.25em] text-brand-ice">{g.catLabel}</p>
              </figcaption>
              <button
                onClick={() => remove(g)}
                disabled={deleting === g.id}
                data-testid={`admin-gallery-delete-${g.id}`}
                className="mt-3 flex w-full items-center justify-center gap-2 border border-white/15 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/60 transition hover:border-brand-red hover:text-brand-red disabled:opacity-50"
              >
                {deleting === g.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                Rimuovi
              </button>
            </figure>
          ))}
        </div>
      )}
    </div>
  );
}
